import { featureCollection } from '@turf/helpers';
import { getHeatmap } from '../../requests/heatmap_requests';
import { getTrailsCheckins, getTrailsRecordings } from '../../requests/trail_requests';
import { pointItemsToGeojson } from '../sources/point_items';
import { addOrUpdateGeojsonSource } from '../sources/geojson_helpers';
import { atMapsToGeojson } from '../sources/at_maps';
import { addLayer, removeLayer, updateLayerProperty, addLayerEvent } from '../layers/layer_helpers';
import { createCheckinHeatmapLayer } from '../layers/heatmap';
import { createPolylineLayer } from '../layers/polylines';
import { removeSource } from '../sources/source_helpers';
import { addPreviewLines, updatePreviewLines, resetPreviewLines } from './preview_lines';
import { addHoverPopupToMap } from '../event_handling/popup_helpers';
import { addRemotePinMarker } from './remote_pin_markers';

const resultsDict = {};
const heatmapPromises = {};

const getPolylinesId = id => {
  return `${id}-heatmap-polylines`;
};

const getPointsId = id => {
  return `${id}-heatmap-points`;
};

const calcPolylinesOpacity = resultsLength => {
  // Lower opacity when more results are present
  return 0.5 / (1.0 + 0.5 * Math.log10(resultsLength || 0));
};

const updateLineOpacity = (map, layerId, resultsLength) => {
  updateLayerProperty(map, layerId, 'paint', 'line-opacity', calcPolylinesOpacity(resultsLength));
};

const resetHeatmapPromises = id => {
  const promises = heatmapPromises[id];
  if (promises.length > 0) {
    promises.forEach(promise => promise.abort());
    promises.length = 0;
  }
};

const setHeatmapPromises = (id, promises) => {
  heatmapPromises[id] = promises;
  Promise.allSettled(promises).finally(() => {
    promises.length = 0;
  });
};

const initHeatmapOverlay = (map, id) => {
  heatmapPromises[id] = {};
  const polylinesId = getPolylinesId(id);
  const pointsId = getPointsId(id);
  if (map.getLayer(polylinesId)) {
    return false;
  }

  addOrUpdateGeojsonSource(map, polylinesId, featureCollection([]));
  addOrUpdateGeojsonSource(map, pointsId, featureCollection([]));
  addLayer(map, createPolylineLayer(polylinesId, polylinesId, { 'line-opacity': 0.5, 'line-width': 12 }));
  addLayer(map, createCheckinHeatmapLayer(pointsId, pointsId));
  addPreviewLines(map);

  return true;
};

const initTrailHeatmapOverlayEvents = (map, id, renderHoverCard) => {
  const polylinesId = getPolylinesId(id);

  const handleClickOn = (e, object) => {
    updatePreviewLines(map, object);
  };

  const handleClickOff = () => {
    resetPreviewLines(map);
  };

  addHoverPopupToMap(map, polylinesId, renderHoverCard, handleClickOn, handleClickOff, {}, true);
  addLayerEvent(map, polylinesId, true, 'mouseenter', () => {
    map.getCanvas().style.cursor = 'pointer';
  });
  addLayerEvent(map, polylinesId, true, 'mouseleave', () => {
    map.getCanvas().style.cursor = '';
  });
};

const initResultHeatmapOverlayEvents = (map, id, renderHoverCard) => {
  const polylinesId = getPolylinesId(id);
  addLayerEvent(map, polylinesId, true, 'click', e => {
    const atMapProps = e.features[0].properties;
    const ID = atMapProps.isVerifiedMap ? atMapProps.trailId : atMapProps.ID;
    const object = resultsDict[id].find(result => result.ID === ID);
    if (object) {
      addRemotePinMarker(map, object, renderHoverCard);
    }
  });
  addLayerEvent(map, polylinesId, true, 'mouseenter', () => {
    map.getCanvas().style.cursor = 'pointer';
  });
  addLayerEvent(map, polylinesId, true, 'mouseleave', () => {
    map.getCanvas().style.cursor = '';
  });
};

const filterAtMapResults = (results, enabledOverlays) => {
  let modResults = results.map(result => {
    let dateField = result.created_at;
    if (!dateField && result.metadata) {
      dateField = result.metadata.created;
    }
    return {
      ...result,
      date: new Date(dateField)
    };
  });
  if (enabledOverlays.includes('after')) {
    const daysAgo = 60;
    const nowInMs = (new Date()).getTime();
    const daysAgoInMs = daysAgo * (24*60*60*1000);
    const afterDate = new Date(nowInMs - daysAgoInMs);
    modResults = modResults.filter(r => r.date > afterDate);
  }
  if (enabledOverlays.includes('limit')) {
    const limit = 50; // num results
    modResults = modResults.sort((a, b) => b.date - a.date).slice(0, limit);
  }
  return modResults;
};

const addTrailHeatmapOverlay = (map, id, trailId, enabledOverlays, renderHoverCard) => {
  const init = initHeatmapOverlay(map, id);
  if (init) {
    initTrailHeatmapOverlayEvents(map, id, renderHoverCard);
  }
  resetHeatmapPromises(id);
  const polylinesId = getPolylinesId(id);
  const pointsId = getPointsId(id);
  const recordingsPromise = getTrailsRecordings(trailId);
  const checkinsPromise = getTrailsCheckins(trailId);
  setHeatmapPromises(id, [recordingsPromise, checkinsPromise]);
  recordingsPromise.then(recordings => {
    recordings = filterAtMapResults(recordings, enabledOverlays);
    addOrUpdateGeojsonSource(map, polylinesId, atMapsToGeojson(recordings, true));
    updateLineOpacity(map, polylinesId, recordings.length);
  });
  checkinsPromise.then(checkins => {
    addOrUpdateGeojsonSource(map, pointsId, pointItemsToGeojson(checkins));
  });
};

const addResultsHeatmapOverlay = (map, id, heatMapResults, enabledOverlays, renderHoverCard) => {
  const results = heatMapResults || []; // heatMapResults will sometimes come in as null
  resultsDict[id] = results;
  const init = initHeatmapOverlay(map, id);
  if (init) {
    initResultHeatmapOverlayEvents(map, id, renderHoverCard);
  }
  resetHeatmapPromises(id);
  const polylinesId = getPolylinesId(id);
  const pointsId = getPointsId(id);
  // Accumulate ids
  const trailIds = [];
  const mapIds = [];
  results.forEach(result => {
    if (result.type === 'trail') {
      trailIds.push(result.ID);
    } else {
      mapIds.push(result.ID);
    }
  });
  // Get heatmaps
  const promises = [];
  if (trailIds.length > 0 || mapIds.length > 0) {
    const mapsPromise = getHeatmap({
      trail_ids: trailIds,
      map_ids: mapIds
    });
    promises.push(mapsPromise);
    mapsPromise.then(maps => {
      // Update heatmap
      addOrUpdateGeojsonSource(map, polylinesId, atMapsToGeojson(maps, true));
      updateLineOpacity(map, polylinesId, maps.length);
    });
  }
  setHeatmapPromises(id, promises);
  addOrUpdateGeojsonSource(map, pointsId, pointItemsToGeojson(results));
};

const removeHeatmapOverlay = (map, id) => {
  delete resultsDict[id];
  delete heatmapPromises[id];
  const polylinesId = getPolylinesId(id);
  const pointsId = getPointsId(id);
  removeLayer(map, polylinesId);
  removeLayer(map, pointsId);
  removeSource(map, polylinesId);
  removeSource(map, pointsId);
};

const heatmapOverlayId = layerId => {
  return `${layerId}Heatmap`; // this format should match format in overlay_configs
};

export { addTrailHeatmapOverlay, addResultsHeatmapOverlay, removeHeatmapOverlay, heatmapOverlayId, filterAtMapResults };
